import Vue from 'vue'
import configs from '@/config'
import {
  STATUS_PAGE_DOWN_POLL_INTERVAL_MS,
  STATUS_PAGE_NOTICE_FLAG,
  STATUS_PAGE_EVENT_FEED_PATH,
  STATUS_PAGE_MONITOR_LIST_PATH,
  STATUS_PAGE_NOT_DOWN_CLASSES,
  STATUS_PAGE_NOTICE_DISMISSED_KEY,
  STATUS_PAGE_NOTICE_POLL_INTERVAL_MS,
  STATUS_PAGE_PROXY_HOSTS,
  STATUS_PAGE_PROXY_PREFIX,
  STATUS_PAGE_URL,
  SYSTEM_STATUS_BAR_FLAG,
  SYSTEM_STATUS_REQUEST_TIMEOUT_MS,
} from '@/constant/systemStatus.constant'

const MAX_DISMISSED_IDS = 20
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
// The feed's end date is "Nov 11, 2026 07:00" in GMT; Date.parse on that shape differs across browsers.
const FEED_DATE_PATTERN = /^([A-Z][a-z]{2}) (\d{1,2}), (\d{4}) (\d{1,2}):(\d{2})$/

const readDismissedIds = () => {
  try {
    const ids = JSON.parse(localStorage.getItem(STATUS_PAGE_NOTICE_DISMISSED_KEY))
    return Array.isArray(ids) ? ids.map(String) : []
  } catch (error) {
    return []
  }
}

export const statusPageNoticeState = Vue.observable({ notice: null, dismissedIds: [] })

// The status page reports the global stack, so an EU outage or an EU user's view of a global one would be wrong.
export const isStatusPageSourceEnabled = (getters) => configs.region !== 'eu'
  && getters['featureToggles/isFeatureEnabled'](SYSTEM_STATUS_BAR_FLAG)
  && getters['featureToggles/isFeatureEnabled'](STATUS_PAGE_NOTICE_FLAG)

let pollTimer = null
let isFetching = false
let isPollEnabled = null
let isDown = false
let onDownChange = () => {}
let isProxyUnavailable = false

const parseFeedDateGmt = (value) => {
  const match = FEED_DATE_PATTERN.exec(String(value || '').trim())
  if (!match) return null
  const [, monthName, day, year, hours, minutes] = match
  const month = MONTHS.indexOf(monthName)
  if (month < 0) return null
  return Date.UTC(Number(year), month, Number(day), Number(hours), Number(minutes))
}

const isActiveAnnouncement = (event, now) => {
  if (!event || (event.type || event.eventType) !== 'announcement') return false
  if (!event.id || !(event.title || event.content)) return false
  const startsAt = Number(event.timestamp) * 1000
  if (!Number.isFinite(startsAt) || startsAt > now) return false
  if (!event.endDateGMT) return true
  const endsAt = parseFeedDateGmt(event.endDateGMT)
  return endsAt !== null && endsAt > now
}

export const pickActiveNotice = (feed, now = Date.now()) => {
  const events = feed && Array.isArray(feed.results) ? feed.results : []
  const latest = events
    .filter((event) => isActiveAnnouncement(event, now))
    .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))[0]
  if (!latest) return null
  return {
    id: String(latest.id),
    title: String(latest.title || ''),
    text: String(latest.content || latest.description || ''),
    endsAt: latest.endDateGMT ? parseFeedDateGmt(latest.endDateGMT) : null,
  }
}

export const hasDownMonitor = (monitorList) => monitorList.data
  .some((monitor) => !STATUS_PAGE_NOT_DOWN_CLASSES.includes(monitor && monitor.statusClass))

const isFeed = (body) => Boolean(body) && Array.isArray(body.results)
const isMonitorList = (body) => Boolean(body) && Array.isArray(body.data)

const readJson = async (url) => {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), SYSTEM_STATUS_REQUEST_TIMEOUT_MS)
  try {
    const response = await fetch(url, { credentials: 'omit', signal: controller.signal })
    if (!response.ok) return { status: response.status, body: null }
    return { status: response.status, body: await response.json().catch(() => null) }
  } catch (error) {
    return { status: null, body: null }
  } finally {
    clearTimeout(timer)
  }
}

// A timeout, 429 or 5xx is transient and the proxy is retried next tick; any other answer means it is not deployed here.
const isProxyMissing = ({ status }) => status !== null && status !== 429 && status < 500

const canUseProxy = () => !isProxyUnavailable && STATUS_PAGE_PROXY_HOSTS.includes(window.location.hostname)

const fetchStatusPage = async (path, isValid) => {
  if (canUseProxy()) {
    const proxied = await readJson(`${STATUS_PAGE_PROXY_PREFIX}${path}`)
    if (isValid(proxied.body)) return proxied.body
    if (isProxyMissing(proxied)) isProxyUnavailable = true
  }
  const { body } = await readJson(`${STATUS_PAGE_URL}${path}`)
  return isValid(body) ? body : null
}

const expireEndedNotice = () => {
  const { notice } = statusPageNoticeState
  if (notice && notice.endsAt !== null && notice.endsAt <= Date.now()) statusPageNoticeState.notice = null
}

const setDown = (nextIsDown) => {
  isDown = nextIsDown
  onDownChange(isDown)
}

// A failed read keeps the last known state: a status-page outage must never raise a bar of its own.
const refreshStatusPage = async () => {
  if (isFetching) return
  isFetching = true
  try {
    const [feed, monitorList] = await Promise.all([
      fetchStatusPage(STATUS_PAGE_EVENT_FEED_PATH, isFeed),
      fetchStatusPage(STATUS_PAGE_MONITOR_LIST_PATH, isMonitorList),
    ])
    if (feed) statusPageNoticeState.notice = pickActiveNotice(feed)
    if (monitorList) setDown(hasDownMonitor(monitorList))
  } finally {
    isFetching = false
  }
}

const stopStatusPageNotice = () => {
  clearTimeout(pollTimer)
  pollTimer = null
  isPollEnabled = null
  statusPageNoticeState.notice = null
  setDown(false)
}

const pollAndReschedule = async () => {
  await refreshStatusPage()
  if (!isPollEnabled || pollTimer) return
  const delayMs = isDown ? STATUS_PAGE_DOWN_POLL_INTERVAL_MS : STATUS_PAGE_NOTICE_POLL_INTERVAL_MS
  pollTimer = setTimeout(() => {
    pollTimer = null
    if (!isPollEnabled()) {
      stopStatusPageNotice()
      return
    }
    expireEndedNotice()
    pollAndReschedule()
  }, delayMs)
}

export const setStatusPageDownHandler = (handler) => {
  onDownChange = handler
}

export const startStatusPageNotice = (isEnabled) => {
  if (isPollEnabled) return
  isPollEnabled = isEnabled
  statusPageNoticeState.dismissedIds = readDismissedIds()
  pollAndReschedule()
}

export const isStatusPageNoticeDismissed = (id) => statusPageNoticeState.dismissedIds.includes(String(id))

export const dismissStatusPageNotice = (id) => {
  const ids = [...statusPageNoticeState.dismissedIds.filter((dismissedId) => dismissedId !== String(id)), String(id)]
    .slice(-MAX_DISMISSED_IDS)
  statusPageNoticeState.dismissedIds = ids
  try {
    localStorage.setItem(STATUS_PAGE_NOTICE_DISMISSED_KEY, JSON.stringify(ids))
  } catch (error) {
    // Storage can be full or blocked; the dismissal still holds for this page load.
  }
}

export const buildStatusPageAnnouncement = ({ isSystemStatusActive, defaultMessage }) => {
  const { notice } = statusPageNoticeState
  if (isSystemStatusActive) {
    return {
      criticalLevel: 3,
      statusPage: {
        key: 'system-status',
        title: notice ? notice.title : '',
        text: notice ? notice.text : defaultMessage,
        noticeId: null,
      },
    }
  }
  if (!notice || isStatusPageNoticeDismissed(notice.id)) return null
  return {
    criticalLevel: 2,
    statusPage: {
      key: `status-page-notice-${notice.id}`,
      title: notice.title,
      text: notice.text,
      noticeId: notice.id,
    },
  }
}
